/**
 * Canonical permission codes — keep in sync with backend `permissions` table and Flyway seeds.
 * UI routes, action registry, and Roles matrix reference these codes.
 */
export const PERMISSION = {
  REPORTING_RELATIONSHIP: {
    VIEW: 'REPORTING_RELATIONSHIP_VIEW',
    CREATE: 'REPORTING_RELATIONSHIP_CREATE',
    EDIT: 'REPORTING_RELATIONSHIP_EDIT',
    DELETE: 'REPORTING_RELATIONSHIP_DELETE',
  },
  EMPLOYEE: {
    VIEW: 'EMPLOYEE_VIEW',
    CREATE: 'EMPLOYEE_CREATE',
    EDIT: 'EMPLOYEE_EDIT',
    DELETE: 'EMPLOYEE_DELETE',
    MANAGE: 'EMPLOYEE_MANAGE',
  },
  HR: {
    VIEW: 'HR_VIEW',
    MANAGE: 'HR_MANAGE',
    ADMIN: 'HR_ADMIN',
    EMPLOYEE_MANAGE: 'HR_EMPLOYEE_MANAGE',
    ESS_ACCESS: 'HR_ESS_ACCESS',
  },
  LEAVE: {
    VIEW: 'HR_LEAVE_VIEW',
    CREATE: 'HR_LEAVE_CREATE',
    EDIT: 'HR_LEAVE_EDIT',
    APPROVE: 'HR_LEAVE_APPROVE',
    MANAGE: 'HR_LEAVE_MANAGE',
  },
  ROLE: {
    VIEW: 'ROLE_VIEW',
    MANAGE: 'ROLE_MANAGE',
  },
  APPROVAL: {
    VIEW: 'APPROVAL_VIEW',
    ACT: 'APPROVAL_ACT',
    MANAGE: 'APPROVAL_MANAGE',
  },
} as const;

export type PermissionCode =
  | (typeof PERMISSION.REPORTING_RELATIONSHIP)[keyof typeof PERMISSION.REPORTING_RELATIONSHIP]
  | (typeof PERMISSION.EMPLOYEE)[keyof typeof PERMISSION.EMPLOYEE]
  | (typeof PERMISSION.HR)[keyof typeof PERMISSION.HR]
  | (typeof PERMISSION.LEAVE)[keyof typeof PERMISSION.LEAVE]
  | (typeof PERMISSION.ROLE)[keyof typeof PERMISSION.ROLE]
  | (typeof PERMISSION.APPROVAL)[keyof typeof PERMISSION.APPROVAL]
  | string;

/** Standard CRUD+ action types aligned with backend routes.json and UiScreenActions. */
export type PermissionAction =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'export'
  | 'import'
  | 'process';

export type ResourceActionDefinition = {
  resource: string;
  label: string;
  path: string;
  actions: Partial<Record<PermissionAction, readonly string[]>>;
};

/** Metadata-driven action registry — maps screens to permission codes for UI + Roles matrix. */
export const ACTION_REGISTRY: ResourceActionDefinition[] = [
  {
    resource: 'reporting_relationship',
    label: 'Reporting hierarchy',
    path: '/hr/org',
    actions: {
      view: [PERMISSION.REPORTING_RELATIONSHIP.VIEW, PERMISSION.HR.VIEW],
      create: [
        PERMISSION.REPORTING_RELATIONSHIP.CREATE,
        PERMISSION.HR.MANAGE,
        PERMISSION.HR.EMPLOYEE_MANAGE,
      ],
      edit: [
        PERMISSION.REPORTING_RELATIONSHIP.EDIT,
        PERMISSION.HR.MANAGE,
        PERMISSION.HR.EMPLOYEE_MANAGE,
      ],
      delete: [
        PERMISSION.REPORTING_RELATIONSHIP.DELETE,
        PERMISSION.HR.MANAGE,
        PERMISSION.HR.EMPLOYEE_MANAGE,
      ],
    },
  },
  {
    resource: 'employee',
    label: 'Employee directory',
    path: '/hr/employees',
    actions: {
      view: [PERMISSION.HR.VIEW, PERMISSION.EMPLOYEE.VIEW],
      create: [PERMISSION.HR.EMPLOYEE_MANAGE, PERMISSION.HR.MANAGE, PERMISSION.EMPLOYEE.MANAGE],
      edit: [PERMISSION.HR.EMPLOYEE_MANAGE, PERMISSION.HR.MANAGE, PERMISSION.EMPLOYEE.MANAGE],
      delete: [PERMISSION.HR.MANAGE, PERMISSION.EMPLOYEE.MANAGE],
    },
  },
];

const registryByPath = new Map(ACTION_REGISTRY.map((entry) => [entry.path, entry]));

export function getActionRegistryEntry(path: string): ResourceActionDefinition | undefined {
  const normalized = path.split('?')[0].replace(/\/+$/, '') || '/';
  return registryByPath.get(normalized);
}
